import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useUser } from "../use-user";
import { RootState } from "../../store";
import { usersApi, billingApi } from "../../api";
import { IUser } from "../../data-access";
import { appConfig } from "../../app-config";

export interface IOnboardingStep {
  key: string;
  title: string;
  description: string;
  done: boolean;
  ctaLabel: string;
  onAction: () => void;
}

const UPWORK_PROFILE_URL = "https://www.upwork.com/freelancers/";
const UPWORK_FIND_WORK_URL = "https://www.upwork.com/nx/find-work/";

const dismissKey = (userId?: string): string =>
  `upwex_onboarding_dismissed_${userId ?? "anon"}`;

const openExternal = (url: string): void => {
  window.open(url, "_blank", "noreferrer");
};

// Steps 2 & 3 read fresh /api/user (upwork_profile), since the Redux user can be
// stale after a sync. Fetch once and cache it across the card + sidebar instances.
let freshUserCache: IUser | null = null;
let freshUserPromise: Promise<IUser | null> | null = null;

const fetchFreshUser = (): Promise<IUser | null> => {
  if (freshUserCache) return Promise.resolve(freshUserCache);
  if (!freshUserPromise) {
    freshUserPromise = usersApi
      .getMe()
      .then((r) => {
        freshUserCache = r.data;
        return r.data;
      })
      .catch(() => null);
  }
  return freshUserPromise;
};

// Step 3 (first AI use) checks the ALL-TIME usage history, not ai_credits_used:
// the latter is a current-period counter (resets each cycle, and on trial it can
// even be negative), so it wrongly marks the step undone for users who already
// used AI in a past period. usage-history `total` = lifetime AI actions.
let usageTotalCache: number | null = null;
let usageTotalPromise: Promise<number> | null = null;

const fetchUsageTotal = (): Promise<number> => {
  if (usageTotalCache !== null) return Promise.resolve(usageTotalCache);
  if (!usageTotalPromise) {
    usageTotalPromise = billingApi
      .getUsageHistory(1, 1)
      .then((r) => {
        usageTotalCache = r?.total ?? 0;
        return usageTotalCache;
      })
      .catch(() => 0);
  }
  return usageTotalPromise;
};

/**
 * Activation onboarding: 3 core steps to reach first value.
 * Step completion is derived live from real signals; only the "dismissed"
 * flag is persisted (localStorage, per user — no backend).
 */
// Dev preview: localStorage.setItem("upwex_onboarding_preview","1") forces the
// onboarding to show with all steps incomplete (for screenshots / QA).
const isPreviewMode = (): boolean => {
  try {
    return localStorage.getItem("upwex_onboarding_preview") === "1";
  } catch {
    return false;
  }
};

export const useOnboarding = () => {
  const { currentUser } = useUser();
  // Single source of truth — written once by SidebarUserPanel's detection,
  // so the dashboard card and the sidebar indicator never disagree.
  const extensionInstalled = useSelector(
    (s: RootState) => s.generic.extensionInstalled
  ) as boolean | undefined;

  const preview = isPreviewMode();
  const userId = currentUser?.id;
  const [dismissed, setDismissed] = useState<boolean>(false);
  const [freshUser, setFreshUser] = useState<IUser | null>(null);
  const [userLoaded, setUserLoaded] = useState<boolean>(false);
  const [usageTotal, setUsageTotal] = useState<number>(0);
  const [usageLoaded, setUsageLoaded] = useState<boolean>(false);

  useEffect(() => {
    let active = true;
    fetchFreshUser().then((u) => {
      if (active) {
        setFreshUser(u);
        setUserLoaded(true);
      }
    });
    fetchUsageTotal().then((t) => {
      if (active) {
        setUsageTotal(t);
        setUsageLoaded(true);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  // Prefer fresh /api/user data, fall back to the Redux user before it loads
  const user = freshUser ?? currentUser;

  useEffect(() => {
    if (!userId) return;
    try {
      setDismissed(localStorage.getItem(dismissKey(userId)) === "1");
    } catch {
      setDismissed(false);
    }
  }, [userId]);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(dismissKey(userId), "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  }, [userId]);

  const steps: IOnboardingStep[] = [
    {
      key: "extension",
      title: "Install the Upwex extension",
      description: "Add Upwex to Chrome to use the AI tools right on Upwork.",
      done: !preview && extensionInstalled === true,
      ctaLabel: "Install",
      onAction: () => openExternal(appConfig.extensionDownloadLink),
    },
    {
      key: "sync",
      title: "Sync your Upwork profile",
      description:
        "Open your Upwork profile and hit Sync so the AI can tailor your proposals.",
      // The synced profile object (from /api/user) is the source of truth —
      // null until a profile is really synced.
      done: !preview && !!user?.upwork_profile?.userId,
      ctaLabel: "Open Upwork",
      onAction: () => openExternal(UPWORK_PROFILE_URL),
    },
    {
      key: "first_ai",
      title: "Try Upwex AI on a job",
      description:
        "Check your feed, generate an AI cover letter, or answer Q&A on Upwork.",
      // ALL-TIME AI usage: any usage-history record means the user has used AI
      // at least once (unlike ai_credits_used, which resets each period).
      done: !preview && usageTotal > 0,
      ctaLabel: "Find a job",
      onAction: () => openExternal(UPWORK_FIND_WORK_URL),
    },
  ];

  const completed = steps.filter((s) => s.done).length;
  const total = steps.length;

  return {
    steps,
    completed,
    total,
    allDone: completed === total,
    // Render only once ALL async signals (extension detection + /api/user +
    // usage history) have settled — so we never flash the onboarding before we
    // know what's missing.
    ready:
      preview ||
      (extensionInstalled !== undefined && userLoaded && usageLoaded),
    dismissed: preview ? false : dismissed,
    dismiss,
  };
};
